import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Modal, Form, Input, Select,
  Space, Typography, Tag, Tooltip, App, Breadcrumb,
} from 'antd';
import {
  PlusOutlined, EditOutlined, RightOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAuth } from '../context/AuthContext';
import { coursesApi } from '../api/courses';
import { Course, Status } from '../api/types';

const { Title } = Typography;

const STATUS_COLORS: Record<Status, string> = {
  Enabled: 'success',
  Disabled: 'warning',
  Deleted: 'error',
};

const STATUS_OPTIONS: Status[] = ['Enabled', 'Disabled', 'Deleted'];

const CoursesPage: React.FC = () => {
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Course | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const api = useCallback(() => coursesApi(apiClient!), [apiClient]);

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().getList();
      setCourses(res.data.items);
    } catch {
      message.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [api, message]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const openCreate = () => {
    setEditing(null);
    form.setFieldsValue({ title: '', description: '', status: 'Enabled' });
    setModalOpen(true);
  };

  const openEdit = (course: Course) => {
    setEditing(course);
    form.setFieldsValue(course);
    setModalOpen(true);
  };

  const handleSave = async (values: Omit<Course, 'id'>) => {
    setSaving(true);
    try {
      if (editing) {
        await api().update({ ...values, id: editing.id });
        message.success('Course updated');
      } else {
        await api().create(values);
        message.success('Course created');
      }
      setModalOpen(false);
      fetchCourses();
    } catch {
      message.error('Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<Course> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      filters: STATUS_OPTIONS.map(s => ({ text: s, value: s })),
      onFilter: (value, record) => record.status === value,
      render: (s: Status) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: '',
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit course">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => { e.stopPropagation(); openEdit(record); }}
            />
          </Tooltip>
          <Button
            size="small"
            type="link"
            icon={<RightOutlined />}
            onClick={() => navigate(`/courses/${record.id}/modules`)}
          >
            Modules
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Breadcrumb items={[{ title: 'Courses' }]} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>Courses</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Course
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={courses}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          onRow={(record) => ({ style: { cursor: 'pointer' } })}
        />
      </Space>

      <Modal
        title={editing ? 'Edit Course' : 'New Course'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        destroyOnClose
        okText={editing ? 'Save' : 'Create'}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="e.g. Introduction to Programming" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Short description of the course" />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={STATUS_OPTIONS.map(s => ({
                value: s,
                label: <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CoursesPage;
